// src/components/modals/ModalLayer.tsx
//
// Plan 2026-08-04 (nested auth modal opens behind the source wizard).
//
// A modal that raises its own z-index above Mantine's default breaks every
// modal opened from inside it: the child falls back to the default `modal`
// elevation (200) and lands underneath its own parent's overlay, unreachable.
// That is invisible until someone clicks the button, which is how it shipped.
//
// This publishes the enclosing layer's z-index so a nested modal can match it.
//
// MATCH the layer, do not exceed it. Mantine's elevations are
// `app: 100, modal: 200, popover: 300, overlay: 400` — at equal z-index the
// painting order falls to DOM order, and a Mantine `Modal` mounts its portal
// when it opens, i.e. after its parent's. Later portal, same elevation, paints
// on top. That is exactly how `AddAuthorizationModal` opens
// `BigQueryAuthorizationCreateModal` today, both at 200.
//
// Layer + 1 is the tempting version and it is wrong: popovers (`Select`
// dropdowns, `Tooltip`) default to 300, so a modal pushed to 301 hides its own
// dropdowns behind its own content — one stacking bug traded for another.

import { createContext, useContext, type ReactNode } from "react";

const ModalLayerContext = createContext<number | undefined>(undefined);

/**
 * Publishes the z-index of the modal layer its children are rendered inside;
 * nested modals read it via {@link useModalLayerZIndex}.
 *
 * `AppModal` does both halves itself (it publishes its layer and matches the
 * enclosing one), so a modal built on it needs neither. Wrap content in this by
 * hand only for a raised surface that is NOT an `AppModal` — e.g. a Drawer with
 * a non-default `zIndex` — or a confirm opened from inside it lands behind it.
 */
export function ModalLayerProvider({
  zIndex,
  children,
}: {
  zIndex: number | undefined;
  children: ReactNode;
}) {
  return (
    <ModalLayerContext.Provider value={zIndex}>
      {children}
    </ModalLayerContext.Provider>
  );
}

/**
 * The z-index of the nearest enclosing modal layer, or `undefined` outside one.
 *
 * `undefined` is the correct value to hand Mantine — it means "use the default
 * elevation", which is what a modal that is not nested inside a raised layer
 * should do.
 */
export function useModalLayerZIndex(): number | undefined {
  return useContext(ModalLayerContext);
}
